import type { AnyEventObject, DoneInvokeEvent } from "xstate"
import { assign, createMachine } from "xstate"

import type { NonceOutput } from "@/query/graphql"
import type { Chain } from "@/web3/hooks/useMultichainAccount"

export enum IdentityMachineEvents {
  INITIALIZE_CONTEXT = "INITIALIZE_CONTEXT",

  WALLET_CONNECTED = "WALLET_CONNECTED",
  WALLET_DISCONNECTED = "WALLET_DISCONNECTED",

  AUTHENTICATE = "AUTHENTICATE",
  AUTHENTICATED = "AUTHENTICATED",
  LOGGED_OUT = "LOGGED_OUT",

  ACKNOWLEDGE_INITIAL_SMART_CONTRACT_WALLET_WARNING = "ACKNOWLEDGE_INITIAL_SMART_CONTRACT_WALLET_WARNING",
  CANCEL_SMART_CONTRACT_WALLET_AUTHENTICATION = "CANCEL_SMART_CONTRACT_WALLET_AUTHENTICATION",
}

type EventObject = {
  // ui / ux
  shouldPromptAuthenticationAfterWalletConnection: boolean
  isUserFirstVisit: boolean
  redirectTo: string | undefined

  // wallet
  chain: Chain | null

  // authentication
  nonceResult: NonceOutput | null
  message: string | Uint8Array | null
  signature: string | null
  safeTransactionUrl: string | null
  signatureError: string | null
}

const initialContext: EventObject = {
  shouldPromptAuthenticationAfterWalletConnection: false,
  isUserFirstVisit: false,
  redirectTo: undefined,

  chain: null,
  safeTransactionUrl: null,

  nonceResult: null,
  message: null,
  signature: null,
  signatureError: null,
}

const idleWallet = (from: "eoa" | "smartContractWallet") => ({
  tags: ["idleWallet"],

  always: {
    target: "authenticate",
    cond: (context: EventObject) =>
      context.shouldPromptAuthenticationAfterWalletConnection,
  },

  on: {
    AUTHENTICATE: {
      target: "authenticate",
      actions: assign({
        redirectTo: (_: EventObject, event: AnyEventObject) => event.redirectTo,
      }),
    },
    AUTHENTICATED: {
      target:
        from === "eoa"
          ? "authenticate.authenticated"
          : "authenticate.authenticated.complete",
    },
  },
})

const fetchNonce = {
  initial: "idle",

  invoke: {
    src: "fetchNonce",
    onDone: {
      target: "generateMessage",
      actions: assign({
        nonceResult: (_: EventObject, event: DoneInvokeEvent<NonceOutput>) =>
          event.data,
      }),
    },
    onError: {
      target: ".error",
    },
  },

  states: {
    idle: {},
    error: {
      tags: ["renderErrorToast"],
    },
  },
}

const generateMessage = (from: "eoa" | "smartContractWallet") => ({
  initial: "idle",

  invoke: {
    src: "generateMessage",
    onDone: {
      target: from === "eoa" ? "signMessage" : "generateSafeTransactionUrl",
      actions: assign({
        message: (
          _: EventObject,
          event: DoneInvokeEvent<string | Uint8Array>,
        ) => event.data,
      }),
    },
    onError: {
      target: ".error",
    },
  },

  states: {
    idle: {},
    error: {
      tags: ["renderErrorToast"],
    },
  },
})

const signMessage = (from: "eoa" | "smartContractWallet") => ({
  initial: "idle",

  invoke: {
    src: "signMessage",
    onDone: {
      target: "login",
      actions: assign({
        signature: (_: EventObject, event: DoneInvokeEvent<string>) =>
          event.data,
      }),
    },
    onError: [
      {
        target: ".error",
        cond: (_: EventObject, event: DoneInvokeEvent<Error>) =>
          !event.data.message.includes("User rejected the request"),
        actions: assign({
          signatureError: (_: EventObject, event: DoneInvokeEvent<Error>) =>
            event.data.message,
        }),
      },
      {
        target: `#identity.walletConnected.${from}.idleWallet`,
        actions: assign({
          shouldPromptAuthenticationAfterWalletConnection: (
            _: EventObject,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            event: DoneInvokeEvent<Error>,
          ) => false,
        }),
      },
    ],
  },

  on: {
    CANCEL_SMART_CONTRACT_WALLET_AUTHENTICATION: {
      target: `#identity.walletConnected.${from}.idleWallet`,
      actions: assign({
        shouldPromptAuthenticationAfterWalletConnection: (_: EventObject) =>
          false,
      }),
    },
  },

  states: {
    idle: {},
    error: {},
  },
})

const login = (from: "eoa" | "smartContractWallet") => ({
  initial: "idle",

  invoke: {
    src: "login",
    onDone: {
      target: "authenticated",
    },
  },

  on: {
    CANCEL_SMART_CONTRACT_WALLET_AUTHENTICATION: {
      target: `#identity.walletConnected.${from}.idleWallet`,
      actions: assign({
        shouldPromptAuthenticationAfterWalletConnection: (_: EventObject) =>
          false,
      }),
    },
  },

  states: {
    idle: {},
  },
})

const backToIdleWallet = (from: "eoa" | "smartContractWallet") => ({
  target: `#identity.walletConnected.${from}.idleWallet`,
  actions: assign({
    shouldPromptAuthenticationAfterWalletConnection: (_: EventObject) => false,
  }),
})

const identityMachine = createMachine<EventObject>({
  id: "identity",
  predictableActionArguments: true,
  initial: "anonymous",
  context: initialContext,
  states: {
    anonymous: {
      on: {
        INITIALIZE_CONTEXT: {
          actions: assign({
            shouldPromptAuthenticationAfterWalletConnection: (_, event) =>
              event.shouldPromptAuthenticationAfterWalletConnection,
            isUserFirstVisit: (_: EventObject) => true,
            redirectTo: (_, event) => event.redirectTo,
          }),
        },

        WALLET_CONNECTED: {
          target: "walletConnected",
          actions: assign({
            chain: (_, event) => event.chain,
          }),
        },
      },
    },

    walletConnected: {
      initial: "idle",

      on: {
        WALLET_DISCONNECTED: {
          target: "anonymous",
        },
      },

      states: {
        idle: {
          initial: "idle",

          invoke: {
            src: "checkIsSmartContractWallet",

            onDone: [
              {
                target: "smartContractWallet",
                cond: (_, event) => event.data,
              },
              {
                target: "eoa",
                cond: (_, event) => !event.data,
              },
            ],

            onError: {
              target: ".error",
            },
          },

          states: {
            idle: {},

            error: {
              tags: ["renderErrorToast"],
            },
          },
        },

        eoa: {
          initial: "idleWallet",

          states: {
            idleWallet: idleWallet("eoa"),

            authenticate: {
              initial: "fetchNonce",

              states: {
                fetchNonce,

                generateMessage: generateMessage("eoa"),

                signMessage: signMessage("eoa"),

                login: login("eoa"),

                rejected: {},

                error: {},

                authenticated: {
                  tags: ["authenticated"],

                  on: {
                    LOGGED_OUT: backToIdleWallet("eoa"),
                  },
                },
              },
            },
          },
        },
        smartContractWallet: {
          initial: "idleWallet",

          states: {
            idleWallet: idleWallet("smartContractWallet"),

            authenticate: {
              initial: "displaySmartContractWalletWarning",

              states: {
                displaySmartContractWalletWarning: {
                  on: {
                    ACKNOWLEDGE_INITIAL_SMART_CONTRACT_WALLET_WARNING: {
                      target: "fetchNonce",
                    },
                    CANCEL_SMART_CONTRACT_WALLET_AUTHENTICATION:
                      backToIdleWallet("smartContractWallet"),
                  },
                },

                fetchNonce,

                generateMessage: generateMessage("smartContractWallet"),

                generateSafeTransactionUrl: {
                  initial: "idle",

                  invoke: {
                    src: "generateSafeTransactionUrl",
                    onDone: {
                      target: "signMessage",
                      actions: assign({
                        safeTransactionUrl: (
                          _: EventObject,
                          event: DoneInvokeEvent<string>,
                        ) => event.data,
                      }),
                    },
                    onError: {
                      target: ".error",
                    },
                  },

                  states: {
                    idle: {},

                    error: {
                      tags: ["renderErrorToast"],
                    },
                  },
                },

                signMessage: signMessage("smartContractWallet"),

                login: login("smartContractWallet"),

                rejected: {},

                error: {},

                authenticated: {
                  tags: ["authenticated"],

                  initial: "complete",

                  states: {
                    complete: {},
                  },

                  on: {
                    LOGGED_OUT: backToIdleWallet("smartContractWallet"),
                  },
                },
              },
            },
          },
        },
      },
    },
  },
})

export default identityMachine
